<template>
	<v-combobox
		ref="combobox"
		v-model="phoneValue"
		dense
		filled
		outlined
		:clearable="!isDialpad"
		:label="phoneLabel"
		:rules="computedRules"
		:placeholder="phonePlaceholder"
		:items="filteredItems"
		item-value="value"
		item-text="title"
		:return-object="false"
		@update:search-input="onUpdateSearch"
	>
		<template slot="prepend-inner">
			<vue-tel-input-vuetify
				v-model="phoneValue"
				:only-countries="availableCountries"
				:default-country="phoneDefaultCountry"
				append-icon="mdi-chevron-down"
				dense
				disabled-fetching-country
				select-classes="select-phone"
				text-field-classes="input-phone"
				@input="onPhoneInput"
			/>
		</template>
	</v-combobox>
</template>

<script>
import { RequiredValidator, PhoneValidator } from "@/validators";
import { AVAILABLE_COUNTRIES } from "../../common/const";

export default {
	name: "ComboboxPhone",

	props: {
		items: {
			type: Array,
			default: () => []
		},

		value: {
			type: String,
			default: ""
		},

		label: {
			type: String,
			default: undefined
		},

		placeholder: {
			type: String,
			default: undefined
		},

		isDialpad: {
			type: Boolean,
			default: false
		},

		backgroundColor: {
			type: String,
			default: undefined
		},

		defaultCountry: {
			type: String,
			default: undefined
		},

		valid: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			availableCountries: AVAILABLE_COUNTRIES,
			phoneObject: {},
			rules: {
				required: RequiredValidator,
				phone: PhoneValidator
			}
		};
	},

	computed: {
 		phoneValue: {
 			get() {
 				return this.value;
 			},

 			set(value) {
 				this.$emit("input", value);
 				this.emitValidationLive(value);
 			}
 		},

		filteredItems() {
			const checkphone = this.items.some((item) =>
				item.value.includes(this.phoneValue)
			);
			const match = this.items.filter((item) =>
				item.value.includes(this.phoneValue)
			);

			if (this.phoneValue !== "" && checkphone) {
				return match;
			} else {
				return [];
			}
		},

		phoneLabel() {
			return this.label;
		},

		phonePlaceholder() {
			return this.placeholder;
		},

		phoneBackground() {
			return this.backgroundColor;
		},

		phoneDefaultCountry() {
			return this.defaultCountry;
		},

		computedRules() {
			return [
				v => (this.isDialpad && !v) ? true : this.rules.required(v, "telephone"),
				v => !v ? true : this.rules.phone(this.phoneObject, "phone"),
				() => !this.phoneValue ? true : this.isValid()
			];
		}
	},

	destroyed() {
		this.phoneObject = {};
	},

 	methods: {
		focus() {
			this.$refs.combobox?.focus();
		},

		onPhoneInput(_, phoneObject) {
			this.sanitizePhoneNumber(phoneObject);
			this.phoneObject = phoneObject;
			this.emitValidationLive(this.phoneValue);
		},

		emitValidationLive(value) {
			// For dialpad: empty input should not appear invalid, but not be considered valid for calling
			if (this.isDialpad && !value) {
				this.$emit("validate", false);
				return;
			}
			if (!value) {
				// Non-dialpad contexts may still want required rule to fire; we keep validate false
				this.$emit("validate", false);
				return;
			}
			const phoneOk = this.rules.phone(this.phoneObject, "phone") === true;
			const duplicateOk = this.isValid() === true;

			this.$emit("validate", phoneOk && duplicateOk);
		},

 		isValid() {
 			const checkphone = this.items.some((item) =>
 				item.value.includes(this.phoneValue)
 			);

 			if (checkphone) {
 				return this.valid || this.$t("error.validator.phone-exist");
 			} else {
 				return true;
 			}
 		},

 		onUpdateSearch(value) {
 			this.$emit("update-search", value);
 		},

		sanitizePhoneNumber(phoneObject) {
			const rawInput = phoneObject?.number?.input;
			const nationCode = phoneObject?.country?.dialCode;
			// strips null values
			let phoneInput = (rawInput == null || rawInput === "null" || rawInput === "undefined") ? "" : String(rawInput);
			
			phoneInput = phoneInput.replace(/[()\s-]/g, "");
			if (phoneInput.startsWith("+")) {
				this.phoneValue = "+" + phoneInput.substring(1).replace(/[^0-9]/g, "");
			} else if (phoneInput) {
				const digits = phoneInput.replace(/[^0-9]/g, "");
				const safeCode = nationCode && nationCode !== "null" ? nationCode : "";

				this.phoneValue = safeCode ? `+${safeCode}${digits}` : `+${digits}`;
			} else {
				this.phoneValue = "";
			}
		}
 	}
};
</script>

<style scoped>
	::v-deep .v-input__prepend-inner {
		margin: 0px !important;
	}
	::v-deep .v-input__prepend-inner .v-input__append-inner {
		margin-top: 5px !important;
	}
	::v-deep .v-input__prepend-inner .v-select__selections {
		padding-top: 4px !important;
	}
	::v-deep .select-phone .v-input__slot {
		background-color: transparent !important;
	}
	::v-deep .select-phone .v-input__slot::before,
	::v-deep .select-phone .v-input__slot::after,
	::v-deep .select-phone .v-text-field__details,
	::v-deep .input-phone {
		display: none !important;
	}

	::v-deep .mdi-menu-down::before {
		content: "";
	}
</style>
